/**
 * The Practice Topics pages of an exam's workspace, laid out as Mock Tests is: a
 * Beginnings, a Foundations and a Beyond page, and one page with all three (the
 * Overview's "By type" row opens it, as its Mock Tests row opens all the mocks).
 *
 * Beginnings holds the practice topics the workspace has always listed, and any
 * section paper filed under beginnings. Foundations and Beyond hold the section
 * papers of their tier (utils/sectionPapers.js sectionPaperTier).
 */

export const PRACTICE_TOPICS_LABEL = 'Practice Topics';

export const PRACTICE_TOPICS_ALL_PAGE = 'practice';

/** [{ page, tier, label, level }] in menu order; `level` is how many bars the tier icon fills. */
export const PRACTICE_TOPIC_TIER_PAGES = [
  { page: 'practice-beginnings', tier: 'beginnings', label: 'Beginnings', level: 1 },
  { page: 'practice-foundations', tier: 'foundations', label: 'Foundations', level: 2 },
  { page: 'practice-beyond', tier: 'beyond', label: 'Beyond', level: 3 },
];

/** Page id -> the title the page and the phone's top bar show. */
export const PRACTICE_TOPIC_PAGE_TITLES = {
  [PRACTICE_TOPICS_ALL_PAGE]: PRACTICE_TOPICS_LABEL,
  ...Object.fromEntries(
    PRACTICE_TOPIC_TIER_PAGES.map(({ page, label }) => [page, `${PRACTICE_TOPICS_LABEL}: ${label}`])
  ),
};

/** The tier page for a page id, or null when the id is not one of them. */
export function practiceTopicTierPage(page) {
  return PRACTICE_TOPIC_TIER_PAGES.find((entry) => entry.page === page) || null;
}

/**
 * One Overview row's figures for some practice topics and some section papers:
 * { meta, pct, score }. `papers` is a sectionPaperProgress result (or null). The
 * score is the papers' mean percentage correct, as topics carry no score.
 */
export function practiceProgressSummary({
  topicsDone = 0,
  topicCount = 0,
  papers = null,
  emptyMeta = 'No topics available yet',
} = {}) {
  const paperTotal = papers?.total || 0;
  const paperDone = papers?.done || 0;
  // "of 1 topics", as the rows have always read, so a row's wording does not change with its count.
  const topicPart = `${topicsDone} of ${topicCount} topics`;
  const paperPart = `${paperDone} of ${paperTotal} papers`;
  let meta = emptyMeta;
  if (topicCount && paperTotal) meta = `${topicPart}, ${paperPart} completed`;
  else if (topicCount) meta = `${topicPart} completed`;
  else if (paperTotal) meta = `${paperPart} completed`;
  const total = topicCount + paperTotal;
  return {
    meta,
    pct: total ? Math.round(((topicsDone + paperDone) / total) * 100) : 0,
    score: papers?.score != null ? `${papers.score}%` : '—',
  };
}
